import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useInmobiliariaImpersonation } from "@/contexts/InmobiliariaImpersonationContext";

/** Inmobiliaria con la que trabaja el portal, resuelta en el servidor (`get_inmob_contexto`). */
export interface InmobContexto {
  persona_id: number;
  nombre: string;
  es_sozu: boolean;
  comision_pct: number | null;
  /** Dueña o supervisor de la inmobiliaria, o Super Admin: puede actuar sobre sus agentes. */
  es_gestor: boolean;
  rol_id: number | null;
  ver_como: boolean;
}

/**
 * Argumentos "ver como" de las RPC del portal: con impersonación, `p_auth_user_id` del
 * impersonado; sin ella, nada (el servidor usa la sesión).
 */
export function argsVerComo<T extends Record<string, unknown>>(args: T, verComoUid: string | null): T {
  return verComoUid ? { ...args, p_auth_user_id: verComoUid } : args;
}

/**
 * Contexto del Portal Inmobiliaria: la inmobiliaria del usuario (o la del impersonado, en "ver
 * como"). La decide la BD: dueña -> su persona efectiva; Supervisor Inmobiliaria -> la que
 * supervisa; personal interno -> SOZU. Sin permiso de lectura en el portal, `personaId` es null.
 */
export function useInmobContexto() {
  const { profile } = useAuth();
  const { impersonatedAuthUserId, isImpersonating, impersonacionPendiente } = useInmobiliariaImpersonation();
  const verComoUid = isImpersonating ? impersonatedAuthUserId : null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["inmob-contexto", profile?.email ?? null, profile?.rol_id ?? null, profile?.id_persona ?? null, verComoUid],
    enabled: !!profile?.email && !impersonacionPendiente,
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<InmobContexto | null> => {
      const { data, error } = await (supabase as any).rpc("get_inmob_contexto", argsVerComo({}, verComoUid));
      if (error) return null;
      return (data ?? null) as InmobContexto | null;
    },
  });

  return {
    contexto: data ?? null,
    personaId: data?.persona_id ?? null,
    /** auth_user_id del impersonado o null: va como `p_auth_user_id` en cada RPC de lectura. */
    verComoUid,
    /** Con "ver como" (o mientras se valida) nada se escribe. */
    soloLectura: isImpersonating || impersonacionPendiente,
    isLoading: impersonacionPendiente || (!!profile?.email && isLoading),
    isError,
  };
}

/** Compatibilidad: id de la inmobiliaria del portal (ver `useInmobContexto`). */
export function useInmobiliariaPersonaId() {
  const { personaId, isLoading } = useInmobContexto();
  return { personaId, isLoading };
}
